import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { API_BASE_URL } from "../config";
import {
    authLogin,
    authRegister,
    authVerify,
    authResendVerification,
    authForgotPassword,
    authResetPassword,
    authMagicLink,
    authMagicLogin,
    setAccountDisabledHandler,
} from "./api";

const AuthCtx = createContext(null);

function normalizeUser(data) {
    if (!data || typeof data !== "object") return null;
    // Modern UserPublic from /auth/me uses picture_url; legacy /account uses picture.
    // Mirror both so consumers (LeftMenu, AccountPage, HomePage) work either way.
    if (data.picture_url && !data.picture) {
        return { ...data, picture: data.picture_url };
    }
    if (data.picture && !data.picture_url) {
        return { ...data, picture_url: data.picture };
    }
    return data;
}

async function fetchAccount(signal) {
    const res = await fetch(`${API_BASE_URL}/auth/me`, {
        credentials: "include",
        signal,
    });
    if (!res.ok) return null;
    const body = await res.json().catch(() => null);
    return normalizeUser(body);
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [suspended, setSuspended] = useState(false);

    const refresh = useCallback(async () => {
        const data = await fetchAccount();
        setUser(data);
        return data;
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchAccount();
                if (!cancelled) setUser(data);
            } catch {
                if (!cancelled) setUser(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Wire the api-layer interceptor so any call returning 403
    // account_disabled clears local state and flips us into the
    // suspended branch — caught by NavigationGuard in App.
    useEffect(() => {
        setAccountDisabledHandler(() => {
            setUser(null);
            setSuspended(true);
        });
        return () => setAccountDisabledHandler(null);
    }, []);

    const consume = (data) => {
        const normalized = normalizeUser(data);
        if (normalized) setUser(normalized);
        return normalized;
    };

    const login = useCallback(async (creds) => {
        const data = await authLogin(creds);
        return consume(data);
    }, []);

    const register = useCallback(async (body) => {
        return authRegister(body);
    }, []);

    const verify = useCallback(async (token) => {
        const data = await authVerify(token);
        return consume(data);
    }, []);

    const resendVerification = useCallback(async (email) => {
        return authResendVerification(email);
    }, []);

    const forgotPassword = useCallback(async (email) => {
        return authForgotPassword(email);
    }, []);

    const resetPassword = useCallback(async (token, password) => {
        const data = await authResetPassword(token, password);
        return consume(data);
    }, []);

    const requestMagicLink = useCallback(async (email) => {
        return authMagicLink(email);
    }, []);

    const consumeMagicLink = useCallback(async (token) => {
        const data = await authMagicLogin(token);
        return consume(data);
    }, []);

    const logout = useCallback(() => {
        setUser(null);
        window.location.href = `${API_BASE_URL}/logout`;
    }, []);

    const value = {
        user,
        loading,
        suspended,
        refresh,
        login,
        register,
        verify,
        resendVerification,
        forgotPassword,
        resetPassword,
        requestMagicLink,
        consumeMagicLink,
        logout,
    };

    return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthCtx);
    if (!ctx) {
        throw new Error("useAuth must be used inside <AuthProvider>");
    }
    return ctx;
}
